import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useDebounce } from '../../../../../hooks';
import { getManufacturers } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type { VisaoGeralElasticidadeMultiFilterSelectItem, VisaoGeralElasticidadeMultiFilterSelectList } from '../types';
import { buildFilterRequestPayload } from '../utils';

const createInitialSelectList = (): VisaoGeralElasticidadeMultiFilterSelectList => ({
  list: [],
  cache: [],
  value: [],
});

const mapFilterResponseToOptions = (items: string[]): VisaoGeralElasticidadeMultiFilterSelectItem[] =>
  (items || []).map(item => ({
    label: item,
    value: item,
  }));

type FetchManufacturerParams = {
  query?: string;
  excludeSelfFromPayload?: boolean;
};

export const useManufacturerFilter = () => {
  const filtersBody = useVisaoGeralElasticidadeStore(state => state.filtersBody);
  const updateSharedFilters = useVisaoGeralElasticidadeStore.getState().updateSharedFilters;
  const debounce = useDebounce(500);

  const [manufacturers, setManufacturers] = useState<VisaoGeralElasticidadeMultiFilterSelectList>(createInitialSelectList);
  const filtersBodyRef = useRef(filtersBody);
  const manufacturersRef = useRef(manufacturers);

  filtersBodyRef.current = filtersBody;
  manufacturersRef.current = manufacturers;

  const setList = useCallback((response: string[]) => {
    const newList = mapFilterResponseToOptions(response);
    const current = manufacturersRef.current;
    const removeDuplicate = newList.filter(item => !current.value.includes(item.value));
    const concatList = current.value.length ? current.cache.concat(removeDuplicate) : newList;
    const cache = concatList.filter(item => current.value.includes(item.value));

    setManufacturers(prev => ({
      ...prev,
      list: concatList,
      cache,
    }));
  }, []);

  const mutation = useMutation({
    mutationFn: async ({ query = '', excludeSelfFromPayload = false }: FetchManufacturerParams) => {
      const requestBody = excludeSelfFromPayload
        ? buildFilterRequestPayload('manufacturers', filtersBodyRef.current, query)
        : {
            ...filtersBodyRef.current,
            query,
          };

      const response = (await getManufacturers(requestBody)) as string[];

      if (response?.length) {
        setList(response);
      }

      return response;
    },
  });

  const fetchManufacturers = useCallback(
    (query = '', excludeSelfFromPayload = false) => {
      mutation.mutate({ query, excludeSelfFromPayload });
    },
    [mutation],
  );

  const handleOpen = useCallback(() => {
    fetchManufacturers('', true);
  }, [fetchManufacturers]);

  const onSearchFilter = useCallback(
    (inputValue: string) => {
      debounce(() => {
        fetchManufacturers(inputValue, true);
      });
    },
    [debounce, fetchManufacturers],
  );

  const handleSelectFilter = useCallback(
    (values: string[]) => {
      const current = manufacturersRef.current;
      const cache = current.list.filter(item => values.includes(item.value));

      updateSharedFilters({ manufacturers: values });
      setManufacturers(prev => ({
        ...prev,
        list: prev.list,
        cache,
        value: values,
      }));
    },
    [updateSharedFilters],
  );

  const applySavedFilterValues = useCallback(
    (values: string[]) => {
      const options = mapFilterResponseToOptions(values);

      updateSharedFilters({ manufacturers: values });
      setManufacturers({
        value: values,
        cache: options,
        list: options,
      });
    },
    [updateSharedFilters],
  );

  const handleClearFilters = useCallback(() => {
    updateSharedFilters({ manufacturers: [] });
    setManufacturers(createInitialSelectList());
  }, [updateSharedFilters]);

  const fetchManufacturersRef = useRef(fetchManufacturers);
  fetchManufacturersRef.current = fetchManufacturers;

  useEffect(() => {
    fetchManufacturersRef.current('', true);
  }, []);

  return {
    manufacturers,
    isLoading: mutation.isPending,
    filterHandlers: {
      handleOpen,
      onSearchFilter,
      handleSelectFilter,
      applySavedFilterValues,
      handleClearFilters,
    },
  };
};
